import { gql } from '@apollo/client';
import type { LocalDocumentResponseSet } from '@eaco/forms-ui-web';
import { PUBLIC_RESPONSE_SET_FIELDS } from './publicForm';

/**
 * Shared field set on every result mutation / query — includes the
 * snapshotted versionContent so a returning filler stays on the
 * version they started, not whatever's currently published.
 */
const RESULT_FIELDS = gql`
  fragment ResultFields on FormDocumentResult {
    id
    formDocumentId
    formName
    documentVersionId
    versionNumber
    versionContent
    requiredOverride
    submission
    publicSlugUsed
    status
    responses
    submittedAt
    createdAt
    updatedAt
  }
`;

export const FORM_DOCUMENT_RESULT_QUERY = gql`
  ${RESULT_FIELDS}
  ${PUBLIC_RESPONSE_SET_FIELDS}
  query formDocumentResult($id: ID!, $secretToken: String) {
    formDocumentResult(id: $id, secretToken: $secretToken) {
      ...ResultFields
      versionResponseSets {
        ...PublicResponseSetFields
      }
    }
  }
`;

export const START_FORM_DRAFT = gql`
  ${RESULT_FIELDS}
  mutation startFormDraft($input: StartFormDraftInput!) {
    startFormDraft(input: $input) {
      success
      message
      secretToken
      result {
        ...ResultFields
      }
    }
  }
`;

// Autosave returns a minimal projection so each keystroke doesn't pull
// versionContent back across the wire.
export const SAVE_FORM_DRAFT = gql`
  mutation saveFormDraft($input: SaveFormDraftInput!) {
    saveFormDraft(input: $input) {
      success
      message
      result {
        id
        status
        updatedAt
      }
    }
  }
`;

export const SUBMIT_FORM_RESULT = gql`
  ${RESULT_FIELDS}
  mutation submitFormResult($input: SubmitFormResultInput!) {
    submitFormResult(input: $input) {
      success
      message
      result {
        ...ResultFields
      }
    }
  }
`;

export interface FormDocumentResult {
  id: string;
  formDocumentId: string;
  formName: string | null;
  documentVersionId: string;
  versionNumber: number | null;
  versionContent: Record<string, unknown> | null;
  versionResponseSets?: LocalDocumentResponseSet[] | null;
  requiredOverride: boolean | null;
  /** Per-form submission settings (submit label, confirmation, live score). */
  submission?: { showLiveScore?: boolean } | null;
  publicSlugUsed: string | null;
  status: 'DRAFT' | 'SUBMITTED';
  responses: Record<string, unknown> | null;
  submittedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}
